import React, { useState } from "react";

import { useQuery } from "@apollo/client";
import { Close as CloseIcon, Search as SearchIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import isEmpty from "lodash/isEmpty";
import { makeStyles, withStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { SelectTemplateDialogListItemFragment } from "@/gql/graphql";
import useSearchText from "@/hooks/useSearchText";

import SelectTemplateDialogListItem from "./SelectTemplateDialogListItem";

type Props = {
  open: boolean;
  onClose: () => void;
  onSelectTemplate: (template: SelectTemplateDialogListItemFragment) => void;
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignContent: "center",
    padding: theme.spacing(5, 5),
    overflowY: "hidden",
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    margin: theme.spacing(2, 3, 8, 3),
  },
  headerText: {
    fontSize: "24px",
    fontWeight: "bold",
  },
  option: {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
    marginBottom: "16px",
  },
  optionField: {
    marginBottom: "14px",
  },
  templates: {
    overflowY: "auto",
    // Right margin and padding to offset scrollbar
    marginRight: theme.spacing(-5),
    paddingRight: theme.spacing(5),
  },
}));

const StyledButton = withStyles(Button, {
  root: {
    marginTop: "24px",
    width: "109px",
    alignSelf: "center",
  },
});

const StyledTemplateButton = withStyles(Button, (theme) => ({
  root: {
    padding: theme.spacing(0),
    margin: theme.spacing(0),
    borderRadius: 4,
  },
  label: {
    textTransform: "initial",
  },
}));

const selectTemplateDialogQuery = graphql(`
  query SelectTemplateDialog($name: String) {
    mealPlanTemplates(name: $name) {
      nodes {
        ...SelectTemplateDialogListItem
      }
    }
  }
`);

const SelectTemplateDialog: React.FC<Props> = ({
  open,
  onClose,
  onSelectTemplate,
}) => {
  const { classes } = useStyles();
  const [selectedTemplate, setSelectedTemplate] =
    useState<SelectTemplateDialogListItemFragment>();
  const [searchText, setSearchText] = useSearchText({ debounce: 500 });
  const { loading, data } = useQuery(selectTemplateDialogQuery, {
    variables: { name: searchText },
  });
  const templates = data?.mealPlanTemplates.nodes ?? [];
  const isTemplatesEmpty = isEmpty(templates);

  function reset() {
    setSearchText("");
    setSelectedTemplate(undefined);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function handleSubmit() {
    if (selectedTemplate) onSelectTemplate(selectedTemplate);
    handleClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Typography className={classes.headerText}>
            Select a template
          </Typography>
          <IconButton onClick={handleClose} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box className={classes.option}>
          <TextField
            label="Search a Template"
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            variant="standard"
          />
        </Box>
        <Box className={classes.templates}>
          {loading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
              }}
            >
              <CircularProgress variant="indeterminate" size={24} />
            </Box>
          )}
          {!loading && isTemplatesEmpty && (
            <Typography variant="body2" color="textSecondary" align="center">
              You have no templates available.
            </Typography>
          )}
          {templates.map((template) => (
            <>
              <StyledTemplateButton
                key={template.id}
                fullWidth
                variant="text"
                onClick={() => setSelectedTemplate(template)}
              >
                <SelectTemplateDialogListItem
                  template={template}
                  selected={selectedTemplate?.id === template.id}
                />
              </StyledTemplateButton>
              <Box height={24} />
            </>
          ))}
        </Box>

        <StyledButton
          disabled={isTemplatesEmpty || !selectedTemplate}
          onClick={() => handleSubmit()}
        >
          Apply
        </StyledButton>
      </Box>
    </Dialog>
  );
};

export default SelectTemplateDialog;
